import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiConfig } from "../config/api.config";

// ─────────────────────────────────────────────────────────────────
// Estado global de sesión (en memoria mientras la pestaña esté abierta)
// Si el backend guarda los blobs transformados por sesión/BD,
// este objeto sólo guarda la referencia local para el resumen.
// ─────────────────────────────────────────────────────────────────
const SESSION = { ingresos: null, egresos: null };

const STEPS = [
  {
    key: "ingresos",
    step: 1,
    title: "Paso 1 – Ingresos",
    description: "Carga el archivo de ingresos. Al transformar, quedará guardado automáticamente para el resumen.",
    endpoint: "/api/transformar/transformar-ingresos/",
    downloadName: "IngresoTransformado.xlsx",
    sessionKey: "ingresos",
    accept: ".xlsx,.xls,.csv",
    icon: "📥",
    color: "#2563eb",
    colorLight: "#eff6ff",
    colorBorder: "#bfdbfe",
  },
  {
    key: "egresos",
    step: 2,
    title: "Paso 2 – Egresos",
    description: "Carga el archivo de egresos. Al transformar, quedará guardado automáticamente para el resumen.",
    endpoint: "/api/transformar/transformar-egresos/",
    downloadName: "EgresoTransformado.xlsx",
    sessionKey: "egresos",
    accept: ".xlsx,.xls,.csv",
    icon: "📤",
    color: "#7c3aed",
    colorLight: "#f5f3ff",
    colorBorder: "#ddd6fe",
  },
];

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────
const descargarBlob = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

const isExcelContentType = (ct = "") =>
  ct.includes("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") ||
  ct.includes("application/octet-stream");

// ─────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────
export default function TransformadorCSV() {
  const navigate = useNavigate();

  // Archivo seleccionado por paso
  const [files, setFiles] = useState({ ingresos: null, egresos: null });

  // Estado de carga por paso + resumen
  const [loading, setLoading] = useState({ ingresos: false, egresos: false, resumen: false });

  // Qué pasos ya fueron transformados con éxito
  const [done, setDone] = useState({ ingresos: false, egresos: false });

  // Mensajes de error por paso
  const [errors, setErrors] = useState({ ingresos: null, egresos: null, resumen: null });

  const setErr = (key, msg) => setErrors((p) => ({ ...p, [key]: msg }));
  const clearErr = (key) => setErrors((p) => ({ ...p, [key]: null }));

  // ── Transformar ingresos / egresos ─────────────────────────────
  const handleTransform = useCallback(async (step) => {
    const { key, endpoint, downloadName, sessionKey } = step;
    const file = files[key];

    if (!file) { setErr(key, "Selecciona un archivo primero."); return; }
    clearErr(key);

    const formData = new FormData();
    formData.append("file", file);

    try {
      setLoading((p) => ({ ...p, [key]: true }));

      const res = await fetch(apiConfig.getUrl(endpoint), {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `Error HTTP ${res.status}`);
      }

      const ct = res.headers.get("content-type") || "";
      if (!isExcelContentType(ct)) {
        const txt = await res.text();
        throw new Error("El servidor no devolvió un Excel válido: " + txt.substring(0, 120));
      }

      const blob = await res.blob();
      if (!blob || blob.size === 0) throw new Error("El archivo descargado está vacío.");

      // ① Guardar en sesión para reutilizar en resumen
      SESSION[sessionKey] = new File([blob], downloadName, { type: blob.type });

      // ② Descargar automáticamente
      descargarBlob(blob, downloadName);

      // ③ Marcar paso como completado
      setDone((p) => ({ ...p, [key]: true }));

    } catch (err) {
      console.error(`❌ Error en ${key}:`, err);
      setErr(key, err.message || "Error procesando archivo.");
    } finally {
      setLoading((p) => ({ ...p, [key]: false }));
    }
  }, [files]);

  // ── Generar resumen (sin subir nada) ───────────────────────────
  const handleResumen = useCallback(async () => {
    if (!SESSION.ingresos || !SESSION.egresos) {
      setErr("resumen", "Primero debes transformar los ingresos y los egresos en los pasos 1 y 2.");
      return;
    }
    clearErr("resumen");

    const formData = new FormData();
    formData.append("ingresos", SESSION.ingresos);
    formData.append("egresos", SESSION.egresos);

    try {
      setLoading((p) => ({ ...p, resumen: true }));

      const res = await fetch(apiConfig.getUrl("/api/transformar/resumen-unificado/"), {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `Error HTTP ${res.status}`);
      }

      const ct = res.headers.get("content-type") || "";
      if (!isExcelContentType(ct)) {
        const txt = await res.text();
        throw new Error("El servidor no devolvió un Excel válido: " + txt.substring(0, 120));
      }

      const blob = await res.blob();
      if (!blob || blob.size === 0) throw new Error("El archivo del resumen está vacío.");

      descargarBlob(blob, "ResumenUnificado.xlsx");

    } catch (err) {
      console.error("❌ Error en resumen:", err);
      setErr("resumen", err.message || "Error generando resumen.");
    } finally {
      setLoading((p) => ({ ...p, resumen: false }));
    }
  }, []);

  // ─────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────
  const bothDone = done.ingresos && done.egresos;

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", padding: "2rem", fontFamily: "'Inter','Segoe UI',sans-serif" }}>

      {/* Botón volver */}
      <button
        type="button"
        onClick={() => navigate("/hacienda-presupuesto")}
        style={{ background: "#e5e7eb", color: "#111827", border: "none", borderRadius: 10, padding: "0.7rem 1.1rem", cursor: "pointer", fontWeight: 600, marginBottom: "1.5rem", fontSize: 13 }}
      >
        ← Volver a Migraciones Hacienda
      </button>

      {/* Header */}
      <div style={{ marginBottom: "1.75rem" }}>
        <h1 style={{ margin: 0, color: "#111827", fontSize: 22, fontWeight: 800 }}>Transformador Presupuestal</h1>
        <p style={{ marginTop: "0.4rem", color: "#6b7280", fontSize: 13 }}>
          Sigue los 3 pasos en orden. El resumen se genera automáticamente con los archivos ya procesados.
        </p>
      </div>

      {/* ── PASOS 1 y 2 ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "1rem", marginBottom: "1rem" }}>
        {STEPS.map((step) => {
          const isDone    = done[step.key];
          const isLoading = loading[step.key];
          const hasFile   = !!files[step.key];
          const err       = errors[step.key];

          return (
            <div key={step.key} style={{
              border: `1.5px solid ${isDone ? "#86efac" : step.colorBorder}`,
              borderRadius: 14,
              padding: "1.25rem",
              background: isDone ? "#f0fdf4" : "#ffffff",
              boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
              transition: "border-color .3s, background .3s",
            }}>
              {/* Título del paso */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 22 }}>{isDone ? "✅" : step.icon}</span>
                <h2 style={{ margin: 0, color: isDone ? "#16a34a" : step.color, fontSize: 16, fontWeight: 800 }}>
                  {step.title}
                </h2>
                {isDone && (
                  <span style={{ marginLeft: "auto", fontSize: 11, background: "#dcfce7", color: "#16a34a", padding: "2px 10px", borderRadius: 20, fontWeight: 700 }}>
                    Listo ✓
                  </span>
                )}
              </div>

              <p style={{ color: "#4b5563", fontSize: 13, marginBottom: 14, lineHeight: 1.5 }}>
                {step.description}
              </p>

              {/* Input archivo */}
              <label style={{
                display: "block",
                border: `2px dashed ${hasFile ? step.color : "#d1d5db"}`,
                borderRadius: 10,
                padding: "14px 12px",
                cursor: "pointer",
                background: hasFile ? step.colorLight : "#fafafa",
                textAlign: "center",
                fontSize: 13,
                color: hasFile ? step.color : "#9ca3af",
                transition: "all .2s",
              }}>
                <input
                  type="file"
                  accept={step.accept}
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    setFiles((p) => ({ ...p, [step.key]: f }));
                    if (f) clearErr(step.key);
                    // Si ya estaba marcado como done y vuelve a subir, resetear
                    if (f) setDone((p) => ({ ...p, [step.key]: false }));
                  }}
                />
                {hasFile
                  ? <>📎 <strong>{files[step.key].name}</strong></>
                  : "Haz clic o arrastra el archivo aquí"}
              </label>

              {/* Botón transformar */}
              <button
                type="button"
                disabled={!hasFile || isLoading}
                onClick={() => handleTransform(step)}
                style={{
                  marginTop: 14,
                  width: "100%",
                  background: isDone ? "#16a34a" : step.color,
                  color: "#fff",
                  border: "none",
                  borderRadius: 10,
                  padding: "0.75rem",
                  cursor: (!hasFile || isLoading) ? "not-allowed" : "pointer",
                  fontWeight: 700,
                  fontSize: 13,
                  opacity: (!hasFile || isLoading) ? 0.6 : 1,
                  transition: "background .2s",
                }}
              >
                {isLoading ? "⏳ Procesando..." : isDone ? "↩ Volver a transformar" : "⚡ Transformar y descargar"}
              </button>

              {/* Error */}
              {err && (
                <div style={{ marginTop: 10, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#dc2626" }}>
                  ⚠ {err}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── PASO 3: RESUMEN ── */}
      <div style={{
        border: `1.5px solid ${bothDone ? "#a5b4fc" : "#e5e7eb"}`,
        borderRadius: 14,
        padding: "1.25rem",
        background: bothDone ? "#f5f3ff" : "#fff",
        boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
        transition: "border-color .3s, background .3s",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <span style={{ fontSize: 22 }}>📊</span>
          <h2 style={{ margin: 0, color: bothDone ? "#4f46e5" : "#9ca3af", fontSize: 16, fontWeight: 800 }}>
            Paso 3 – Resumen Unificado
          </h2>
          {!bothDone && (
            <span style={{ marginLeft: "auto", fontSize: 11, background: "#f3f4f6", color: "#9ca3af", padding: "2px 10px", borderRadius: 20, fontWeight: 600 }}>
              Esperando pasos 1 y 2
            </span>
          )}
        </div>

        {/* Indicadores de estado de pasos previos */}
        <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
          {[
            { label: "Ingresos transformados", ok: done.ingresos },
            { label: "Egresos transformados",  ok: done.egresos  },
          ].map(({ label, ok }) => (
            <div key={label} style={{
              display: "flex", alignItems: "center", gap: 6,
              background: ok ? "#dcfce7" : "#f3f4f6",
              borderRadius: 20, padding: "4px 12px",
              fontSize: 12, fontWeight: 600,
              color: ok ? "#16a34a" : "#9ca3af",
            }}>
              {ok ? "✅" : "⏳"} {label}
            </div>
          ))}
        </div>

        <p style={{ color: "#4b5563", fontSize: 13, marginBottom: 14, lineHeight: 1.5 }}>
          {bothDone
            ? "Los archivos transformados están listos. Haz clic para generar y descargar el resumen consolidado."
            : "Completa los pasos 1 y 2 para habilitar el resumen. No necesitas subir nada aquí."}
        </p>

        <button
          type="button"
          disabled={!bothDone || loading.resumen}
          onClick={handleResumen}
          style={{
            background: bothDone ? "#4f46e5" : "#d1d5db",
            color: "#fff",
            border: "none",
            borderRadius: 10,
            padding: "0.8rem 2rem",
            cursor: (!bothDone || loading.resumen) ? "not-allowed" : "pointer",
            fontWeight: 700,
            fontSize: 14,
            opacity: (!bothDone || loading.resumen) ? 0.7 : 1,
            transition: "background .2s",
          }}
        >
          {loading.resumen ? "⏳ Generando resumen..." : "📥 Generar resumen unificado"}
        </button>

        {errors.resumen && (
          <div style={{ marginTop: 12, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#dc2626" }}>
            ⚠ {errors.resumen}
          </div>
        )}
      </div>
    </div>
  );
}